import prettyMs from 'pretty-ms'
import type { Glue } from '@/types/api'

/* Dates as the console shows and reads them, to the second. */
export const DATE_TIME = 'YYYY-MM-DD HH:mm:ss'

/* The backup state of a device, and the colour it wears wherever it is shown. Kept in this
 * module, which the entry chunk carries: a module of its own becomes one more chunk to load. */
export type BackupState = 'ok' | 'warning' | 'wait' | 'none'

/* What the device list can be narrowed to. */
export type BackupFilter = BackupState | 'all'

export const BACKUP_COLOR: Record<BackupState, string> = {
  ok: 'success',
  warning: 'warning',
  wait: 'danger',
  none: 'secondary',
}

/* sysUpTime (1.3.6.1.2.1.1.3) is a TimeTicks: hundredths of a second, per the MIB. */
const TICKS_PER_SECOND = 100

/* TimeTicks is an unsigned 32-bit counter that wraps here (under 497 days); a
 * larger number is not a sysUpTime, so it is read as seconds. */
const TIMETICKS_LIMIT = 4294967295

/* Some agents answer with the rendered form rather than the counter:
 * "(1706283044) 197 days, 10:20:30.44", or just "197 days, 10:20:30.44". */
const RENDERED = /(?:(\d+)\s+days?,\s*)?(\d+):(\d{2}):(\d{2})(?:\.(\d{1,2}))?/

const fromTicks = (value: number): number | null => {
  if (!Number.isFinite(value) || value < 0) return null
  return value > TIMETICKS_LIMIT ? value : value / TICKS_PER_SECOND
}

export function uptimeSeconds(raw: unknown): number | null {
  if (typeof raw === 'number') return fromTicks(raw)
  if (typeof raw !== 'string') return null
  const text = raw.trim()
  if (!text) return null
  if (/^\d+$/.test(text)) return fromTicks(Number(text))
  const match = RENDERED.exec(text)
  if (!match) return null
  const [, days, hours, minutes, seconds, hundredths] = match
  return (
    Number(days ?? 0) * 86400 +
    Number(hours) * 3600 +
    Number(minutes) * 60 +
    Number(seconds) +
    Number(hundredths ?? 0) / 100
  )
}

export function duration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '—'
  return prettyMs(seconds * 1e3, { unitCount: 2, secondsDecimalDigits: 0 })
}

/* For a device's SNMP TimeTicks only; the agent uptime in a tenant's ping data is
 * already in seconds and goes through duration(). */
export function uptime(raw: unknown): string {
  return duration(uptimeSeconds(raw))
}

export function uptimeDetail(raw: unknown): string {
  const value = uptimeSeconds(raw)
  if (value === null) return 'The collector reported no usable uptime.'
  const exact = prettyMs(value * 1e3, { verbose: true, secondsDecimalDigits: 0 })
  return `Up ${exact} when the inventory last ran`
}

export function formatBytes(bytes: number, decimals: number): string {
  if (!+bytes) return '0 Bytes'
  const k = 1024,
    sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'],
    i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`
}

/* A form always binds both fields; id 0 means no record, so the box shows it empty. */
export function editableGlue(glue?: Partial<Glue>): Glue {
  return { id: glue?.id || null, url: glue?.url ?? '' }
}

/* The API requires `glue.id` to be a number whenever `glue` is present, so a record
 * with no IT Glue id or URL is sent without `glue`. */
export function withGlue<T extends { glue?: Partial<Glue> }>(
  detail: T,
): Partial<T> {
  const body: Partial<T> = { ...detail }
  const id = Number(body.glue?.id) || 0
  const url = (body.glue?.url ?? '').trim()
  if (!id && url.length === 0) {
    delete body.glue
    return body
  }
  body.glue = { id, url } as T['glue']
  return body
}

/* `glue.url` is user input rendered into a link, and stored rows may hold a
 * `javascript:` URL: anything but http(s) returns undefined, leaving the link inert. */
export function safeHref(url: unknown): string | undefined {
  if (typeof url !== 'string' || url.length === 0) return undefined
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return undefined
    return parsed.href
  } catch {
    return undefined
  }
}
